import React, { forwardRef, useState } from 'react'

import {
  borderStyles,
  coloringStyles,
  flexContainerStyles,
  flexItemStyles,
  layoutStyles,
  paddingStyles,
  positioningStyles,
  sizingStyles,
  spacingStyles,
  themedView,
  type KitViewProps,
  type LayoutEvent
} from '../_support'

/* -----------------------------------------------------------------------------
 * LinearGradient
 *
 * react-native-web-linear-gradient with the kit's named gradients: a View
 * whose background image runs from `start` to `end`, the angle following the
 * View's measured size.
 * -------------------------------------------------------------------------- */

const GradientView = themedView(
  'LinearGradient',
  borderStyles,
  coloringStyles,
  flexContainerStyles,
  flexItemStyles,
  layoutStyles,
  paddingStyles,
  positioningStyles,
  sizingStyles,
  spacingStyles
)

type Point = { x: number; y: number }
type Gradient = {
  start?: Point
  end?: Point
  colors: readonly string[]
  locations?: readonly number[]
  useAngle?: boolean
  angle?: number
}

export const variants = {
  blueVioletGradient: {
    start: { x: 1, y: 0 },
    end: { x: 0, y: 0 },
    colors: ['#C763F7', '#5162F4']
  },
  violetGradient: {
    start: { x: 0, y: 0.5 },
    end: { x: 1, y: 0.5 },
    colors: ['#5162F4', '#A872FF']
  },
  lightVioletGradient: {
    start: { x: 0, y: 0.5 },
    end: { x: 1, y: 0.5 },
    colors: ['#abb2f1', '#d2bcf5']
  },
  yellowGradient: {
    start: { x: 0, y: 0.5 },
    end: { x: 1, y: 0.5 },
    colors: ['#FFE897', '#FFD794']
  },
  orangeGradient: {
    start: { x: 0, y: 0.5 },
    end: { x: 1, y: 0.5 },
    colors: ['#FFC990', '#FEAC88']
  },
  redGradient: {
    start: { x: 0, y: 0.5 },
    end: { x: 1, y: 0.5 },
    colors: ['#FD9E84', '#F97A8A']
  },
  lightBlueAngledGradient: {
    start: { x: 0.25, y: 1.5 },
    end: { x: 0.75, y: -0.5 },
    colors: ['#E3EBF8E6', '#F2F5FC99']
  },
  violetAngledGradient: {
    useAngle: true,
    angle: 21.12,
    locations: [0.07, 0.9687],
    colors: ['#5162F4', '#A872FF']
  },
  paleVioletAngledGradient: {
    useAngle: true,
    angle: 21.12,
    locations: [0.07, 0.9687],
    colors: ['#5162F410', '#A872FF10']
  },
  greenAngledGradient: {
    useAngle: true,
    angle: 25.24,
    colors: ['#01AD8E', '#ADFFBA']
  },
  yellowAngledGradient: {
    useAngle: true,
    angle: 45,
    colors: ['#FFE178', '#FFAD24']
  },
  orangeAngledGradient: {
    useAngle: true,
    angle: 45,
    colors: ['#FFBF73', '#FF7A00']
  },
  redAngledGradient: {
    useAngle: true,
    angle: 45,
    colors: ['#FF906E', '#EB3A45']
  },
  grayOverlayGradient: {
    start: { x: 0, y: 1 },
    end: { x: 0, y: 0 },
    colors: ['#00000099', '#00000000']
  }
} as const satisfies Record<string, Gradient>

export type LinearGradientProps = KitViewProps & {
  variant?: keyof typeof variants
  angle?: number
}

const LinearGradient = forwardRef<HTMLElement, LinearGradientProps>(
  ({ variant = 'violetGradient', angle, style, onLayout, ...rest }, ref) => {
    const gradient: Gradient = {
      ...variants[variant],
      ...(angle ? { useAngle: true, angle } : {})
    }
    const [size, setSize] = useState({ width: 1, height: 1 })

    const start = gradient.start ?? { x: 0.5, y: 0 }
    const end = gradient.end ?? { x: 0.5, y: 1 }
    const cssAngle = gradient.useAngle
      ? `${gradient.angle}deg`
      : `${
          Math.atan2(
            size.width * (end.y - start.y),
            size.height * (end.x - start.x)
          ) +
          Math.PI / 2
        }rad`
    const colors = gradient.colors
      .map((color, index) => {
        const location = gradient.locations?.[index]
        return location ? `${color} ${location * 100}%` : color
      })
      .join(',')

    const measure = (event: LayoutEvent) => {
      setSize({
        width: event.nativeEvent.layout.width,
        height: event.nativeEvent.layout.height
      })
      onLayout?.(event)
    }

    return (
      <GradientView
        ref={ref}
        {...rest}
        onLayout={measure}
        style={[
          style,
          { backgroundImage: `linear-gradient(${cssAngle},${colors})` }
        ]}
      />
    )
  }
)
LinearGradient.displayName = 'LinearGradient'

export default LinearGradient
